import React, { useEffect, useMemo } from 'react';
import { FiShoppingCart } from 'react-icons/fi';

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 4000,
};

const modalStyle = {
  backgroundColor: '#ffffff',
  width: '95%',
  maxWidth: '420px',
  maxHeight: '80vh',
  borderRadius: '14px',
  boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 16px',
  borderBottom: '1px solid #e5e7eb',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};

const listStyle = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  overflowY: 'auto',
};

const listItemStyle = {
  padding: '12px 16px',
  borderBottom: '1px solid #f1f5f9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '8px',
};

const itemNameStyle = { fontWeight: 600 };
const itemMetaStyle = { fontSize: '0.85em', color: '#6b7280', marginTop: '2px' };
// SHOP-134: same unit-price accent color used for this badge elsewhere in
// the app (ChooseCartItemModal's OptionMeta) - see feedback_ui_color_reuse
// memory.
const itemUnitPriceStyle = { fontSize: '0.85em', color: '#ff5722', fontWeight: 700, marginTop: '2px' };

const addToCartButtonStyle = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: '#16a34a',
  padding: '6px',
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
};

const SubcategoryItemsModal = ({
  isOpen,
  onClose,
  category,
  items = [],
  translations,
  language,
  formatCurrency,
  onAddToCart,
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const handleKey = (e) => {
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const subcategoryItems = useMemo(() => {
    if (!category?.name) return [];

    // `items` is already scoped to this category server-side (see
    // /category_items), so only true same-item duplicates need deduping
    // here - the join against ItemMathes can return more than one row for
    // the same productid when it has multiple matches under this category.
    // Dedupe by productid, not name+brand (SHOP-138): productid already
    // uniquely identifies a name+store pairing (CLAUDE.md - hash of
    // name+store), so this still collapses true duplicates while never
    // conflating two different stores selling the same-named product.
    const seenKeys = new Set();
    return items
      .filter((item) => {
        if (!item.name || !item.productid || seenKeys.has(item.productid)) return false;
        seenKeys.add(item.productid);
        return true;
      })
      .sort((a, b) => {
        const nameCompare = a.name.localeCompare(b.name);
        if (nameCompare !== 0) return nameCompare;
        return (a.brand || '').localeCompare(b.brand || '');
      });
  }, [items, category]);

  if (!isOpen) return null;

  const title =
    language === 'es'
      ? category?.name_es || category?.name
      : category?.name;

  return (
    <div style={modalOverlayStyle} onClick={onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: '1.1em', fontWeight: 700 }}>
            {translations?.subcategoryItemsModalTitle || 'Items in this subcategory'}
            {title ? `: ${title}` : ''}
          </h3>
          <button
            onClick={onClose}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: '1.2em',
              cursor: 'pointer',
            }}
            aria-label={translations?.modalClose || 'Close'}
          >
            ✕
          </button>
        </div>

        <ul style={listStyle}>
          {subcategoryItems.map((item) => (
            <li key={item.productid} style={listItemStyle}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={itemNameStyle}>
                  {item.brand?.trim() ? `${item.name} (${item.brand})` : item.name}
                </div>
                {item.detail && <div style={itemMetaStyle}>{item.detail}</div>}
                <div style={itemMetaStyle}>
                  {item.store}
                  {item.unit ? ` · ${item.unit}` : ''}
                  {formatCurrency ? ` · ${formatCurrency(item.price)}` : ''}
                </div>
                {item.unit_price != null && item.unit_price !== item.price && formatCurrency && (
                  <div style={itemUnitPriceStyle}>
                    {formatCurrency(item.unit_price)} / {translations?.unit || 'unit'}
                  </div>
                )}
              </div>

              {onAddToCart && (
                <button
                  type="button"
                  onClick={() => onAddToCart(item)}
                  aria-label={translations?.addToCart || 'Add to cart'}
                  title={translations?.addToCart || 'Add to cart'}
                  style={addToCartButtonStyle}
                >
                  <FiShoppingCart size={18} />
                </button>
              )}
            </li>
          ))}

          {subcategoryItems.length === 0 && (
            <li style={{ padding: '16px', color: '#6b7280' }}>
              {translations?.subcategoryItemsEmpty || 'No active items in this subcategory.'}
            </li>
          )}
        </ul>
      </div>
    </div>
  );
};

export default SubcategoryItemsModal;
